// DetailPageShell — shared layout for object detail pages with tabs.
// Renders: back button, title + lifecycle badge, metadata slot, tab bar, Outlet.
// Used by RiskDetailPage, ControlDetailPage, ActionDetailPage, EventDetailPage.

import { ReactNode } from 'react'
import { useNavigate, Outlet, useLocation } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Tab definition ---

export interface TabDef {
  /** Unique tab identifier. */
  id: string
  /** Display label. */
  label: string
  /** URL path suffix (empty string for the default/detail tab). */
  path: string
  /** Key on the data object for a numeric count badge. */
  countKey?: string | null
  /** Key on the data object for a boolean overdue/warning indicator. */
  overdueKey?: string | null
}

// --- Props ---

export interface DetailPageShellProps {
  /** Page title (object name). */
  title: string
  /** Short human-readable code (e.g. "R-XKQR"). Displayed next to the title. */
  code?: string
  /** Lifecycle state label text. */
  stateLabel: string
  /** Tailwind classes for the lifecycle badge. */
  stateColorClass?: string
  /** URL to navigate back to (e.g. `/t/{slug}/risks`). */
  backUrl: string
  /** Tooltip for back button (e.g. "Back to risk list"). */
  backLabel: string
  /** Tab definitions. */
  tabs: TabDef[]
  /** Base path for tab URL computation (e.g. `/t/{slug}/risks/{id}`). */
  basePath: string
  /**
   * The data object — used to look up countKey and overdueKey values.
   * Typed as Record so it works with any domain response shape.
   */
  data: Record<string, unknown>
  /** Domain-specific metadata rendered between the title row and the tab bar. */
  metadata?: ReactNode
  /** Optional action buttons rendered top-right in the header (e.g. "Move to..."). */
  actions?: ReactNode
}

// --- Component ---

export function DetailPageShell({
  title,
  code,
  stateLabel,
  stateColorClass,
  backUrl,
  backLabel,
  tabs,
  basePath,
  data,
  metadata,
  actions,
}: DetailPageShellProps) {
  const navigate = useNavigate()
  const location = useLocation()

  // Determine active tab from URL
  const suffix = location.pathname.replace(basePath, '') || ''
  const activeTab = tabs.find((t) => t.path === suffix)?.id ?? tabs[0]?.id ?? 'detail'

  function handleTabClick(tab: TabDef) {
    navigate({ to: `${basePath}${tab.path}` })
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="space-y-3 border-b px-6 py-4">
        {/* Back + title row */}
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="mt-0.5 flex-shrink-0"
            onClick={() => navigate({ to: backUrl })}
            title={backLabel}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-semibold">{title}</h1>
              <Badge
                variant="outline"
                className={cn('text-xs', stateColorClass)}
              >
                {stateLabel}
              </Badge>
              {code && (
                <span className="font-mono text-xs text-muted-foreground">{code}</span>
              )}
            </div>
            {metadata && (
              <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {metadata}
              </div>
            )}
          </div>
          {actions && (
            <div className="flex-shrink-0 pt-0.5">
              {actions}
            </div>
          )}
        </div>

        {/* Tab bar */}
        <div className="-mb-4 flex gap-0">
          {tabs.map((tab) => {
            const count = tab.countKey ? (data[tab.countKey] as number | null | undefined) : null
            const overdue = tab.overdueKey ? (data[tab.overdueKey] as boolean | undefined) : false
            return (
              <button
                key={tab.id}
                className={cn(
                  'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground',
                )}
                onClick={() => handleTabClick(tab)}
              >
                {tab.label}
                {count != null && count > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">({count})</span>
                )}
                {overdue && (
                  <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-blue-500" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab content — rendered via Outlet for sub-routes */}
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  )
}
